import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NavbarComponent } from '../navbar/navbar.component';
import { AuthService } from '../service/auth/auth.service';
import { RestService } from '../service/rest/rest.service';
import { AdminRestService } from '../service/rest/admin-rest.service';

@Component({
  selector: 'app-admin-subjects',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, NavbarComponent],
  templateUrl: './admin-subjects.component.html',
  styleUrl: './admin-users.component.css'
})
export class AdminSubjectsComponent implements OnInit {
  @Input() embedded = false;
  sessionId = '';
  content: any[] = [];
  courses: { id: number; name: string }[] = [];
  seeds: { id: number; name: string }[] = [];
  selectedSeedId: number | null = null;
  filterCourseId: number | null = null;
  loading = false;
  error = '';
  message = '';
  detailItem: any = null;
  detailForm: any = {};
  detailSaving = false;
  deleteItem: any = null;
  deleteError = '';
  sortColumn = 'name';
  sortDir: 'asc' | 'desc' = 'asc';

  constructor(
    private authService: AuthService,
    private restService: RestService,
    private adminRest: AdminRestService
  ) {}

  async ngOnInit() {
    const user = this.authService.getUser();
    this.sessionId = user?.sid || '';
    await this.loadSeeds();
    const defaultSeed = await this.adminRest.getAdminSeedDefault(this.sessionId);
    if (defaultSeed?.id != null) {
      this.selectedSeedId = defaultSeed.id;
    } else if (this.seeds.length > 0) {
      this.selectedSeedId = this.seeds[0].id;
    }
    await this.loadCourses();
    await this.load();
  }

  async loadCourses() {
    if (!this.sessionId) return;
    const list = await this.adminRest.getAdminCoursesList(this.sessionId, this.selectedSeedId ?? undefined);
    this.courses = list ?? [];
  }

  async load() {
    if (!this.sessionId) return;
    this.loading = true;
    this.error = '';
    try {
      const list = await this.adminRest.getAdminSubjects(this.sessionId, this.filterCourseId ?? undefined, this.selectedSeedId ?? undefined);
      this.content = list ?? [];
    } catch (e: any) {
      this.error = e?.message || 'Failed to load subjects';
    } finally {
      this.loading = false;
    }
  }

  async loadSeeds() {
    if (!this.sessionId) return;
    const list = await this.adminRest.getAdminSeeds(this.sessionId);
    this.seeds = list ?? [];
  }

  onSeedChange() {
    this.loadCourses();
    this.load();
  }

  onFilterChange() {
    this.load();
  }

  openCreate() {
    this.detailItem = { id: null };
    this.detailForm = { courseId: this.filterCourseId ?? this.courses[0]?.id ?? null, name: '', seedId: this.selectedSeedId, active: 1, examQuestions: null, examTimeMinutes: null };
  }

  openEdit(s: any) {
    this.detailItem = s;
    this.detailForm = { courseId: s.courseId, name: s.name ?? '', seedId: s.seedId ?? null, active: s.active ?? 1, examQuestions: s.examQuestions ?? null, examTimeMinutes: s.examTimeMinutes ?? null };
  }

  closeDetail() {
    this.detailItem = null;
    this.load();
  }

  async save() {
    if (!this.sessionId) return;
    this.detailSaving = true;
    this.error = '';
    try {
      if (this.detailItem?.id == null) {
        if (!this.detailForm.name?.trim() || !this.detailForm.courseId) {
          this.error = 'Course and name are required';
          this.detailSaving = false;
          return;
        }
        if (!this.detailForm.seedId) {
          this.error = 'Seed is required';
          this.detailSaving = false;
          return;
        }
        await this.adminRest.postAdminSubject(this.sessionId, {
          courseId: this.detailForm.courseId,
          name: this.detailForm.name.trim(),
          seedId: this.detailForm.seedId,
          active: this.detailForm.active ?? 1,
          examQuestions: this.detailForm.examQuestions ?? undefined,
          examTimeMinutes: this.detailForm.examTimeMinutes ?? undefined
        });
        this.message = 'Subject created.';
      } else {
        await this.adminRest.putAdminSubject(this.sessionId, this.detailItem.id, {
          courseId: this.detailForm.courseId,
          name: this.detailForm.name,
          seedId: this.detailForm.seedId,
          active: this.detailForm.active,
          examQuestions: this.detailForm.examQuestions,
          examTimeMinutes: this.detailForm.examTimeMinutes
        });
        this.message = 'Subject updated.';
      }
      this.closeDetail();
    } catch (e: any) {
      this.error = e?.message || 'Save failed';
    } finally {
      this.detailSaving = false;
    }
  }

  openDeleteConfirm(s: any) {
    this.deleteItem = s;
    this.deleteError = '';
  }

  closeDeleteConfirm() {
    this.deleteItem = null;
    this.deleteError = '';
  }

  async confirmDelete() {
    if (!this.deleteItem?.id || !this.sessionId) return;
    this.deleteError = '';
    try {
      await this.adminRest.deleteAdminSubject(this.sessionId, this.deleteItem.id);
      this.closeDeleteConfirm();
      this.load();
    } catch (e: any) {
      this.deleteError = e?.error?.error || e?.message || 'Delete failed';
    }
  }

  courseName(id: number) {
    return this.courses.find(c => c.id === id)?.name ?? id;
  }

  get sortedContent(): any[] {
    const col = this.sortColumn;
    const dir = this.sortDir === 'asc' ? 1 : -1;
    return [...this.content].sort((a, b) => {
      let va: any = a[col];
      let vb: any = b[col];
      if (col === 'name') {
        va = (va ?? '') + '';
        vb = (vb ?? '') + '';
        return dir * (va.localeCompare(vb, undefined, { sensitivity: 'base' }));
      }
      if (col === 'active') {
        va = a.active ? 1 : 0;
        vb = b.active ? 1 : 0;
      }
      if (va == null) va = '';
      if (vb == null) vb = '';
      return dir * (va < vb ? -1 : va > vb ? 1 : 0);
    });
  }

  setSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDir = 'asc';
    }
  }

  sortIcon(column: string): string {
    if (this.sortColumn !== column) return 'bi bi-arrow-down-up';
    return this.sortDir === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down';
  }
}
